import React, { useEffect, useState } from 'react';
import { Coupon } from '../core/_models';
import { getCoupons, updateCoupon } from '../core/subscriptionService';
import { CouponFormModal } from './CouponFormModal';
import { ModalToggleCoupon } from './ModalToggleCoupon';

export const CouponsTab: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [toggleTarget, setToggleTarget] = useState<Coupon | null>(null);

  const loadCoupons = () => {
    setLoading(true);
    getCoupons().then((data) => {
      setCoupons(data);
      setLoading(false);
    }).catch(() => {
      setCoupons([]);
      setLoading(false);
    });
  };

  const handleToggleStatus = (coupon: Coupon) => {
    setToggleTarget(coupon);
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='fw-bolder fs-2 mb-0'>Cupons de Desconto</h3>
        </div>
        <div className='card-toolbar'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={() => { setEditingCoupon(null); setShowModal(true); }}
          >
            Criar Cupom
          </button>
        </div>
      </div>
      <div className='card-body py-4'>
        {loading ? (
          <div className='d-flex justify-content-center my-10'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Carregando...</span>
            </div>
          </div>
        ) : coupons.length === 0 ? (
          <div className='d-flex flex-column align-items-center justify-content-center py-15'>
            <i className='bi bi-tag fs-2tx text-gray-300 mb-5'></i>
            <span className='text-gray-500 fw-bold fs-5'>Nenhum cupom encontrado.</span>
            <span className='text-muted fs-7 mt-2'>Os cupons cadastrados aparecerão aqui.</span>
          </div>
        ) : (
          <table className='table align-middle table-row-dashed fs-6 gy-5'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                <th className='min-w-100px'>Código</th>
                <th className='min-w-100px'>Desconto</th>
                <th className='min-w-100px'>Status</th>
                <th className='min-w-100px'>Usos</th>
                <th className='min-w-100px'>Duração</th>
                <th className='min-w-100px'>Expira em</th>
                <th className='text-end min-w-70px'>Ações</th>
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold'>
              {coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td>
                    <span className='badge badge-light-warning fw-bolder fs-7'>{coupon.code}</span>
                  </td>
                  <td>
                    {coupon.discount_type === 'PERCENTAGE'
                      ? `${coupon.discount_value || 0}%`
                      : `R$ ${Number(coupon.discount_value || 0).toFixed(2).replace('.', ',')}`}
                  </td>
                  <td>
                    <div className={`badge badge-light-${coupon.status === 'ATIVO' ? 'success' : coupon.status === 'EXPIRADO' ? 'danger' : 'secondary'} fw-bolder`}>
                      {coupon.status}
                    </div>
                  </td>
                  <td>
                    {coupon.usage_count}{coupon.max_usage ? ` / ${coupon.max_usage}` : ''}
                  </td>
                  <td>
                    {coupon.duration_mode === 'FIRST_ONLY' ? (
                      <span className='badge badge-light-info fw-bolder'>1ª Fatura</span>
                    ) : (
                      <span className='badge badge-light-primary fw-bolder'>Todas</span>
                    )}
                  </td>
                  <td>
                    {coupon.expires_at
                      ? new Date(coupon.expires_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
                      : 'Sem expiração'}
                  </td>
                  <td className='text-end'>
                    <div className='btn-group' role='group'>
                      <button
                        type='button'
                        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                        title='Editar'
                        onClick={() => { setEditingCoupon(coupon); setShowModal(true); }}
                      >
                        <i className='bi bi-pencil fs-4'></i>
                      </button>
                      {coupon.status === 'ATIVO' && (
                        <button
                          type='button'
                          className='btn btn-icon btn-bg-light btn-active-light-danger btn-sm'
                          title='Desativar'
                          onClick={() => handleToggleStatus(coupon)}
                        >
                          <i className='bi bi-toggle-on fs-2 text-success'></i>
                        </button>
                      )}
                      {coupon.status === 'INATIVO' && (
                        <button
                          type='button'
                          className='btn btn-icon btn-bg-light btn-active-light-success btn-sm'
                          title='Reativar'
                          onClick={() => handleToggleStatus(coupon)}
                        >
                          <i className='bi bi-toggle-off fs-2 text-muted'></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && coupons.length > 0 && (
        <div className='card-footer border-0 pt-0 pb-5 d-flex justify-content-between align-items-center'>
          <div className='text-gray-500 fw-bold fs-6'>
            Mostrando 1-{coupons.length} de {coupons.length} cupons
          </div>
          <div className='d-flex align-items-center gap-2'>
            <button className='btn btn-light btn-sm btn-active-light-primary fw-bolder disabled'>
              <i className='bi bi-chevron-left'></i> Anterior
            </button>
            <button className='btn btn-light btn-sm btn-active-light-primary fw-bolder disabled'>
              Próxima <i className='bi bi-chevron-right'></i>
            </button>
          </div>
        </div>
      )}

      <CouponFormModal 
        show={showModal} 
        handleClose={() => setShowModal(false)} 
        coupon={editingCoupon} 
        onSaved={loadCoupons}
      />
      <ModalToggleCoupon
        show={!!toggleTarget}
        coupon={toggleTarget}
        handleClose={() => {
          setToggleTarget(null);
          loadCoupons();
        }}
      />
    </div>
  );
};
